import type { Schemas } from "./api/client";

/**
 * Passkey ceremonies with the browser's own JSON helpers (WebAuthn Level 3,
 * Baseline 2025), so no library is needed (P1-025). A refusal the person
 * caused is a typed result the screens word calmly; anything else is
 * reported as "failed".
 */
export type PasskeyFailure = "cancelled" | "already-registered" | "unsupported" | "failed";
export type PasskeyResult<T> = { ok: true; credential: T } | { ok: false; reason: PasskeyFailure };

export function passkeysSupported(): boolean {
  return (
    typeof PublicKeyCredential !== "undefined" &&
    typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function" &&
    typeof PublicKeyCredential.parseCreationOptionsFromJSON === "function"
  );
}

async function ceremony<T>(run: () => Promise<Credential | null>): Promise<PasskeyResult<T>> {
  if (!passkeysSupported() || !navigator.credentials) return { ok: false, reason: "unsupported" };
  try {
    const credential = (await run()) as PublicKeyCredential | null;
    return credential ? { credential: credential.toJSON() as T, ok: true } : { ok: false, reason: "failed" };
  } catch (error) {
    if (error instanceof DOMException) {
      // NotAllowedError is a cancelled prompt or a timeout; InvalidStateError
      // is a creation on an authenticator that already holds this account.
      if (error.name === "NotAllowedError") return { ok: false, reason: "cancelled" };
      if (error.name === "InvalidStateError") return { ok: false, reason: "already-registered" };
    }
    return { ok: false, reason: "failed" };
  }
}

export function getPasskey(options: Schemas["PasskeyRequestOptions"]) {
  return ceremony<Schemas["PasskeyAssertion"]>(() =>
    navigator.credentials.get({
      publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(options),
    }),
  );
}

export function createPasskey(options: Schemas["PasskeyCreationOptions"]) {
  return ceremony<Schemas["PasskeyAttestation"]>(() =>
    navigator.credentials.create({
      publicKey: PublicKeyCredential.parseCreationOptionsFromJSON(options),
    }),
  );
}

/** Plain words for a ceremony that did not finish. */
export function passkeyFailureMessage(reason: PasskeyFailure): string {
  switch (reason) {
    case "cancelled":
      return "The passkey was not used. Try again.";
    case "already-registered":
      return "This device or security key already has a passkey for your account. Use a different one.";
    case "unsupported":
      return "This browser can't use passkeys. Update Chrome, Edge, Safari or Firefox to the current version, then try again.";
    case "failed":
      return "The passkey could not be used. Try again, or use a different device.";
  }
}
